import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, LockKeyhole, Download, LogOut, Clock } from "lucide-react";
import { Portal } from "@/components/Portal";

const OWNER_ROLES = ["owner", "manager"];

/**
 * Workspace-level billing gate.
 * - expired  -> full-screen soft-lock: app is blocked behind a popup, with a
 *   "Download my audit records" escape hatch (Audit Vault export) + logout.
 * - grace / access ending soon -> a one-time-per-session alert popup.
 * Super-admins and unconfigured tenants are never gated.
 */
export default function BillingGate() {
  const { billing, user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [ackAlert, setAckAlert] = useState(false);

  useEffect(() => {
    try { setAckAlert(sessionStorage.getItem("vb_billing_ack") === "1"); } catch { /* noop */ }
  }, []);

  if (!user || user.role === "super_admin" || !billing) return null;

  const status = billing.status;
  const isOwnerRole = OWNER_ROLES.includes(user.role);

  // --- EXPIRED: soft-lock ---
  if (status === "expired" || status === "suspended") {
    // Allow the audit-vault route while the user is explicitly exporting.
    let exporting = false;
    try { exporting = sessionStorage.getItem("vb_billing_export") === "1"; } catch { /* noop */ }
    const onVault = loc.pathname.startsWith("/app/audit-vault");
    if (exporting && onVault) {
      return (
        <div
          className="fixed top-0 left-0 right-0 z-[9998] flex items-center justify-between gap-3 px-5 py-2 text-sm"
          style={{ background: "#7f1d1d", color: "#fff" }}
          data-testid="billing-export-banner"
        >
          <span className="inline-flex items-center gap-2">
            <LockKeyhole size={14} /> Subscription ended — export-only mode. Download the records you need.
          </span>
          <button className="underline" onClick={logout} data-testid="billing-export-logout">Log out</button>
        </div>
      );
    }
    return (
      <Portal>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
        style={{ background: "rgba(40,22,10,0.75)", backdropFilter: "blur(6px)" }}
        data-testid="billing-lock-modal">
        <div className="vb-card p-8 max-w-lg w-full text-center" style={{ background: "var(--vb-bg)" }}>
          <div className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-4" style={{ background: "#7f1d1d" }}>
            <LockKeyhole size={26} color="#fff" />
          </div>
          <h2 className="vb-heading text-2xl mb-2" style={{ color: "var(--vb-text-2)" }}>
            Your subscription has ended
          </h2>
          <p className="text-sm opacity-80 mb-1">
            Access to <strong>{user.company_name || "your workspace"}</strong> is paused because the
            subscription period {billing.grace_days ? "and grace period have" : "has"} lapsed.
          </p>
          <p className="text-sm opacity-80 mb-6">
            {isOwnerRole
              ? "Please contact Vanguard Beacon support to renew your plan and restore full access."
              : "Please contact your account owner or administrator to renew the subscription."}
          </p>
          <div className="flex flex-col gap-2">
            <button
              className="vb-btn w-full justify-center"
              data-testid="billing-download-records"
              onClick={() => {
                try { sessionStorage.setItem("vb_billing_export", "1"); } catch { /* noop */ }
                nav("/app/audit-vault");
              }}
            >
              <Download size={15} /> Download my audit records
            </button>
            <button className="vb-btn ghost w-full justify-center" onClick={logout} data-testid="billing-lock-logout">
              <LogOut size={15} /> Log out
            </button>
          </div>
          <p className="text-xs opacity-60 mt-4">support@vanguardbeacon.com</p>
        </div>
      </div>
      </Portal>
    );
  }

  // --- GRACE / ending soon: one-time session alert ---
  const daysToEnd = billing.days_until_access_end;
  const graceLeft = billing.days_until_hard_block;
  const showAlert = !ackAlert && (
    status === "grace" || ((status === "trialing" || status === "active") && daysToEnd !== null && daysToEnd <= 7)
  );
  if (!showAlert) return null;

  const inGrace = status === "grace";
  return (
    <Portal>
    <div className="fixed inset-0 z-[9997] flex items-center justify-center p-6"
      style={{ background: "rgba(40,22,10,0.55)" }}
      data-testid="billing-alert-modal">
      <div className="vb-card p-7 max-w-md w-full" style={{ background: "var(--vb-bg)" }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-full grid place-items-center"
            style={{ background: inGrace ? "#b45309" : "var(--vb-text-2)" }}>
            {inGrace ? <AlertTriangle size={20} color="#fff" /> : <Clock size={20} color="#fff" />}
          </div>
          <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>
            {inGrace ? "Subscription lapsed" : "Renewal coming up"}
          </h2>
        </div>
        <p className="text-sm opacity-80 mb-5">
          {inGrace
            ? `Your subscription has ended. You have ${graceLeft ?? 0} day(s) of grace left before the workspace is locked. ${isOwnerRole ? "Contact Vanguard Beacon support to renew." : "Please notify your account owner."}`
            : `Your access window ends in ${daysToEnd} day(s). ${isOwnerRole ? "Please arrange renewal to avoid interruption." : "Please remind your account owner to renew."}`}
        </p>
        <div className="flex justify-end">
          <button
            className="vb-btn"
            data-testid="billing-alert-ack"
            onClick={() => { try { sessionStorage.setItem("vb_billing_ack", "1"); } catch { /* noop */ } setAckAlert(true); }}
          >
            {inGrace ? "I understand" : "Got it"}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}
